import React, { useState, useEffect } from 'react';
import { jobsAPI, usersAPI, teamsAPI } from '../utils/api';
import { Link } from 'react-router-dom';
import { Search, Filter, Plus, Calendar, MapPin, Grid3x3, List } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';

const Jobs = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [jobTypeFilter, setJobTypeFilter] = useState('all');
  const [managers, setManagers] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJobs();
    loadManagers();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, statusFilter, priorityFilter, managerFilter, jobTypeFilter]);

  const loadManagers = async () => {
    try {
      const response = await usersAPI.getManagers();
      setManagers(response.data);
    } catch (error) {
      console.error('Failed to load managers');
    }
  };

  const loadJobs = async () => {
    try {
      const response = await jobsAPI.getAll();
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // Priority order for sorting (urgent first) - must be defined before filterJobs
  const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };

  const filterJobs = () => {
    let filtered = [...jobs];

    if (searchTerm) {
      filtered = filtered.filter(
        (job) =>
          job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.job_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((job) => job.status === statusFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter((job) => job.priority === priorityFilter);
    }

    if (managerFilter !== 'all') {
      filtered = filtered.filter((job) => job.assigned_manager === managerFilter);
    }

    if (jobTypeFilter !== 'all') {
      filtered = filtered.filter((job) => job.job_type === jobTypeFilter);
    }

    // Sort by priority (urgent first) then by creation date
    const sortPriority = { urgent: 0, high: 1, medium: 2, low: 3 };
    filtered.sort((a, b) => {
      const priorityDiff = (sortPriority[a.priority] ?? 4) - (sortPriority[b.priority] ?? 4);
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    setFilteredJobs(filtered);
  };

  // Stats for 1st tier filtering
  const stats = {
    total: jobs.length,
    pending: jobs.filter(j => j.status === 'pending').length,
    in_progress: jobs.filter(j => j.status === 'in_progress').length,
    completed: jobs.filter(j => j.status === 'completed').length,
  };

  const priorityColors = {
    urgent: 'bg-red-500 text-white border-red-600',
    high: 'bg-orange-500 text-white border-orange-600',
    medium: 'bg-yellow-500 text-white border-yellow-600',
    low: 'bg-green-500 text-white border-green-600',
  };

  const statusColors = {
    pending: 'bg-slate-100 text-slate-700 border-slate-200',
    in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="jobs-page" className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Job Management
          </h1>
          <p className="text-slate-600">Track and manage maintenance jobs</p>
        </div>
        {user?.is_super_admin && (
          <Link to="/jobs/new" data-testid="create-job-button">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-sm transition-colors flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Create Job
            </button>
          </Link>
        )}
      </div>

      {/* 1st Tier Filter - Status Counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button
          onClick={() => setStatusFilter('all')}
          className={`bg-white border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            statusFilter === 'all' ? 'border-slate-400 ring-2 ring-slate-200' : 'border-slate-200'
          }`}
        >
          <p className="text-sm text-slate-500">Total Jobs</p>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </button>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`bg-slate-50 border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            statusFilter === 'pending' ? 'border-slate-400 ring-2 ring-slate-200' : 'border-slate-200'
          }`}
        >
          <p className="text-sm text-slate-600">Pending</p>
          <p className="text-2xl font-bold text-slate-700">{stats.pending}</p>
        </button>
        <button
          onClick={() => setStatusFilter('in_progress')}
          className={`bg-blue-50 border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            statusFilter === 'in_progress' ? 'border-blue-400 ring-2 ring-blue-200' : 'border-blue-200'
          }`}
        >
          <p className="text-sm text-blue-600">In Progress</p>
          <p className="text-2xl font-bold text-blue-700">{stats.in_progress}</p>
        </button>
        <button
          onClick={() => setStatusFilter('completed')}
          className={`bg-green-50 border rounded-xl p-4 text-left transition-all hover:shadow-md ${
            statusFilter === 'completed' ? 'border-green-400 ring-2 ring-green-200' : 'border-green-200'
          }`}
        >
          <p className="text-sm text-green-600">Completed</p>
          <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
        </button>
      </div>

      {/* 2nd Tier Filters */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                data-testid="search-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search jobs..."
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
          </div>
          <div>
            <select
              data-testid="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <select
              data-testid="priority-filter"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <select
              data-testid="job-type-filter"
              value={jobTypeFilter}
              onChange={(e) => setJobTypeFilter(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="all">All Types</option>
              <option value="private">Private</option>
              <option value="insurance">Insurance</option>
            </select>
          </div>
          <div>
            <select
              data-testid="manager-filter"
              value={managerFilter}
              onChange={(e) => setManagerFilter(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="all">All Managers</option>
              {managers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            data-testid="grid-view-button"
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Grid3x3 className="w-5 h-5" />
          </button>
          <button
            data-testid="list-view-button"
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' ? (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Header Row - Excel Style */}
          <div className="bg-slate-100 border-b border-slate-300 grid grid-cols-12 gap-0 text-xs font-semibold text-slate-700 uppercase tracking-wider">
            <div className="col-span-1 px-2 py-2.5 border-r border-slate-200">Code</div>
            <div className="col-span-2 px-2 py-2.5 border-r border-slate-200">Job Name</div>
            <div className="col-span-3 px-2 py-2.5 border-r border-slate-200">Description</div>
            <div className="col-span-1 px-2 py-2.5 border-r border-slate-200 text-center">Start</div>
            <div className="col-span-1 px-2 py-2.5 border-r border-slate-200 text-center">End</div>
            <div className="col-span-1 px-2 py-2.5 border-r border-slate-200 text-right">Cost</div>
            <div className="col-span-1 px-2 py-2.5 border-r border-slate-200 text-center">Type</div>
            <div className="col-span-1 px-2 py-2.5 border-r border-slate-200 text-center">Status</div>
            <div className="col-span-1 px-2 py-2.5 text-center">Priority</div>
          </div>
          {/* Data Rows */}
          <div className="divide-y divide-slate-200">
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job, index) => {
                const priorityBorderColors = {
                  urgent: '#ef4444',
                  high: '#f97316',
                  medium: '#eab308',
                  low: '#22c55e',
                };
                const borderColor = priorityBorderColors[job.priority] || '#94a3b8';
                const isLast = index === filteredJobs.length - 1;
                const isEven = index % 2 === 0;
                return (
                  <div 
                    key={job.id} 
                    data-testid={`job-list-row-${job.id}`}
                    className={`grid grid-cols-12 gap-0 hover:bg-blue-50 cursor-pointer items-center relative ${
                      isEven ? 'bg-white' : 'bg-slate-50/50'
                    } ${isLast ? 'rounded-b-xl' : ''}`}
                    onClick={() => window.location.href = `/jobs/${job.id}`}
                  >
                    {/* Priority Color Bar */}
                    <div 
                      className={`absolute left-0 top-0 bottom-0 w-1 ${isLast ? 'rounded-bl-xl' : ''}`}
                      style={{ backgroundColor: borderColor }}
                    />
                    {/* Job Code */}
                    <div className="col-span-1 px-2 py-2 border-r border-slate-100 pl-3">
                      <span className="text-xs font-mono font-bold text-blue-600">
                        {job.job_code || '-'}
                      </span>
                    </div>
                    {/* Job Name */}
                    <div className="col-span-2 px-2 py-2 border-r border-slate-100">
                      <span className="text-sm font-medium text-slate-900 line-clamp-1" title={job.title}>
                        {job.title}
                      </span>
                    </div>
                    {/* Description */}
                    <div className="col-span-3 px-2 py-2 border-r border-slate-100">
                      <span className="text-xs text-slate-600 line-clamp-2" title={job.description}>
                        {job.description || '-'}
                      </span>
                    </div>
                    {/* Start Date */}
                    <div className="col-span-1 px-2 py-2 border-r border-slate-100 text-center">
                      <span className="text-xs text-slate-600">
                        {job.scheduled_date ? format(new Date(job.scheduled_date), 'dd MMM') : '-'}
                      </span>
                    </div>
                    {/* End Date */}
                    <div className="col-span-1 px-2 py-2 border-r border-slate-100 text-center">
                      <span className="text-xs text-slate-600">
                        {job.end_date ? format(new Date(job.end_date), 'dd MMM') : '-'}
                      </span>
                    </div>
                    {/* Cost */}
                    <div className="col-span-1 px-2 py-2 border-r border-slate-100 text-right">
                      <span className="text-xs font-medium text-slate-900">
                        {job.estimated_cost > 0 ? formatCurrency(job.estimated_cost) : '-'}
                      </span>
                    </div>
                    {/* Type */}
                    <div className="col-span-1 px-2 py-2 border-r border-slate-100 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        job.job_type === 'insurance' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {job.job_type || 'private'}
                      </span>
                    </div>
                    {/* Status */}
                    <div className="col-span-1 px-2 py-2 border-r border-slate-100 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        statusColors[job.status] || statusColors.pending
                      }`}>
                        {job.status.replace('_', ' ')}
                      </span>
                    </div>
                    {/* Priority */}
                    <div className="col-span-1 px-2 py-2 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                        priorityColors[job.priority] || priorityColors.low
                      }`}>
                        {job.priority}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-12 text-center text-slate-500">
                No jobs found matching your filters
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredJobs.length > 0 ? (
          filteredJobs.map((job) => {
            const priorityBorderColors = {
              urgent: '#ef4444',  // red-500
              high: '#f97316',    // orange-500
              medium: '#eab308',  // yellow-500
              low: '#22c55e',     // green-500
            };
            return (
              <Link key={job.id} to={`/jobs/${job.id}`} data-testid={`job-item-${job.id}`}>
                <div 
                  className="job-card bg-white border border-slate-200 shadow-sm rounded-xl p-6 hover:shadow-md transition-all h-full"
                  style={{ borderLeft: `4px solid ${priorityBorderColors[job.priority] || '#94a3b8'}` }}
                >
                  <div className="flex items-start justify-between mb-1">
                    {job.job_code && (
                      <span className="text-xs font-mono font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                        {job.job_code}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ml-auto ${
                        priorityColors[job.priority] || priorityColors.low
                      }`}
                    >
                      {job.priority}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-900 text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {job.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{job.description}</p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location}</span>
                    </div>
                    {job.scheduled_date && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4" />
                        <span>{format(new Date(job.scheduled_date), 'MMM dd, yyyy')}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        statusColors[job.status] || statusColors.pending
                      }`}
                    >
                      {job.status.replace('_', ' ')}
                    </span>
                    {job.estimated_cost > 0 && (
                      <span className="text-sm font-medium text-slate-900">{formatCurrency(job.estimated_cost)}</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-slate-500 text-lg">No jobs found matching your filters</p>
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default Jobs;